// Issue - https://github.com/Expensify/App/issues/26719
import type {AppStateStatus} from 'react-native';
import {AppState} from 'react-native';
import type {OnyxEntry, OnyxKey, OnyxMergeInput} from 'react-native-onyx';
import Onyx from 'react-native-onyx';
import type {ValueOf} from 'type-fest';
import * as API from '@libs/API';
import type {
  GetMissingOnyxMessagesParams,
  OpenAppParams,
  //   OpenOldDotLinkParams,
  //   // OpenProfileParams,
  ReconnectAppParams,
} from '@libs/API/parameters';
import {SIDE_EFFECT_REQUEST_COMMANDS, WRITE_COMMANDS} from '@libs/API/types';
// import * as Browser from '@libs/Browser';
// import DateUtils from '@libs/DateUtils';
// import Log from '@libs/Log';
import getCurrentUrl from '@libs/Navigation/currentUrl';
import Navigation from '@libs/Navigation/Navigation';
import setCalendarLocale from '@libs/setCalendarLocale';
// import Performance from '@libs/Performance';
// import * as ReportActionsUtils from '@libs/ReportActionsUtils';
import type CONST from '@src/CONST';
import ONYXKEYS from '@src/ONYXKEYS';
import type {Route} from '@src/ROUTES';
import ROUTES from '@src/ROUTES';
import type * as OnyxTypes from '@src/types/onyx';
// import type {SelectedTimezone} from '@src/types/onyx/UserData';
import type {OnyxData} from '@src/types/onyx/Request';
import type {DateString, UserID} from '@src/types/onyx/OnyxCommon';
import type {User} from 'firebase/auth';
import {updatePreferences} from './Preferences';
import {resolveDuplicationConflictAction} from './RequestConflictUtils';
// import * as Session from './Session';
// import Timing from './Timing';

type Locale = ValueOf<typeof CONST.LOCALES>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let currentUserEmail: string;
let currentUserID: UserID | undefined;
Onyx.connect({
  key: ONYXKEYS.SESSION,
  callback: val => {
    currentUserEmail = val?.email ?? '';
    currentUserID = val?.userID ?? undefined;
  },
});

let isSidebarLoaded: boolean | undefined;
Onyx.connect({
  key: ONYXKEYS.IS_SIDEBAR_LOADED,
  callback: val => (isSidebarLoaded = val),
  initWithStoredValues: false,
});

let preferredLocale: string | undefined;
Onyx.connect({
  key: ONYXKEYS.NVP_PREFERRED_LOCALE,
  callback: val => (preferredLocale = val),
});

/**
 * The allowlist of Onyx keys that survive a sign-out. `Session.cleanupSession()`
 * calls `Onyx.clear(KEYS_TO_PRESERVE)`, which wipes EVERYTHING else — the model
 * is inverted on purpose: a newly added key is cleared-by-default, and only the
 * device-/build-level, non-sensitive keys below are kept. Anything carrying user
 * PII (location, private data incl. DOB, data-visibility, preferences, drinking
 * sessions/drinks/locations, admin feedback/bug lists, …) must NOT be added here.
 *
 * Deliberately NOT preserved — diverges from upstream Expensify, whose
 * `KEYS_TO_PRESERVE` keeps `SESSION`/`CREDENTIALS` because its auth gate reads
 * `SESSION.authToken`. Kiroku authenticates against Firebase
 * (`onAuthStateChanged` for the gate, `getIdToken()` for the API Bearer), so the
 * Onyx `SESSION`/`CREDENTIALS` keys are NOT the source of truth for auth — they
 * only hold the previous user's email/token, which is exactly the cross-account
 * leak we want gone. The next sign-in's `openApp` response repopulates `SESSION`.
 * `IS_LOADING_APP` is likewise cleared (not preserved) so a stale `false` can't
 * open the onboarding/terms readiness gates before the next bootstrap runs.
 */
const KEYS_TO_PRESERVE: OnyxKey[] = [
  // Device identity — documented to survive logout (see Device/generateDeviceID).
  ONYXKEYS.DEVICE_ID,
  // How far this store has been migrated (`migrateOnyx`). A schema number, not
  // account data: sign-out wipes the legacy shapes the migrations look for, so
  // clearing it would only make every post-sign-out launch re-walk the store
  // for nothing.
  ONYXKEYS.ONYX_SCHEMA_VERSION,
  // Connectivity drives the offline request queue + OfflineIndicator; its sole
  // writer (UserConnectionProvider) sits above AuthScreens and doesn't re-emit on
  // sign-out, so clearing it could strand the queue mid-transition.
  ONYXKEYS.NETWORK,
  // Visual/locale preferences are device-level: keep the login screen on the
  // user's theme and language instead of flashing back to the defaults.
  ONYXKEYS.PREFERRED_THEME,
  ONYXKEYS.NVP_PREFERRED_LOCALE,
  // Build-channel flag derived from the installed binary, not the account.
  ONYXKEYS.IS_BETA,
  // Device-level count of tip-jar tips given; cosmetic only (drives the
  // thank-you line on the Support screen) and carries no user data.
  ONYXKEYS.TIPS_GIVEN,
  // Its companion: the tip-jar card's first-open clock and impression count.
  // Same footing (device-level, cosmetic, no user data); clearing it on
  // sign-out would restart the 14-day clock and re-arm a dismissed card.
  ONYXKEYS.TIP_JAR_PROMPT,
];

let resolveIsReadyPromise: () => void;
const isReadyToOpenApp = new Promise<void>(resolve => {
  resolveIsReadyPromise = resolve;
});

function confirmReadyToOpenApp() {
  resolveIsReadyPromise();
}

function setLocale(locale: Locale) {
  if (locale === preferredLocale) {
    return;
  }

  // Update the react-native-calendars global locale synchronously, before the
  // Onyx merge below triggers the locale-driven re-render. The library reads
  // its day-name row from this global at render time (not from a prop), so
  // setting it here means the calendar renders the new locale on the very next
  // render — no remount, no one-frame-stale flash. The per-screen effect in the
  // calendar views still covers the cold-start path (locale hydrated from Onyx
  // without going through this action).
  setCalendarLocale(locale);

  // If user is not signed in, change just locally.
  if (!currentUserID) {
    Onyx.merge(ONYXKEYS.NVP_PREFERRED_LOCALE, locale);
    return;
  }

  // Persist through kiroku-api's preferences endpoint. Its optimistic data
  // also sets NVP_PREFERRED_LOCALE, so the UI switches language immediately.
  updatePreferences({locale});
}

function setLocaleAndNavigate(locale: Locale) {
  setLocale(locale);
  Navigation.goBack();
}

function setSidebarLoaded() {
  if (isSidebarLoaded) {
    return;
  }

  Onyx.set(ONYXKEYS.IS_SIDEBAR_LOADED, true);
  // Performance.markStart(CONST.TIMING.REPORT_INITIAL_RENDER);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let appState: AppStateStatus;
AppState.addEventListener('change', nextAppState => {
  // if (nextAppState.match(/inactive|background/) && appState === 'active') {
  //   Log.info('Flushing logs as app is going inactive', true, {}, true);
  // }
  appState = nextAppState;
});

/**
 * Returns the Onyx data that is used for both the OpenApp and ReconnectApp API commands.
 */
function getOnyxDataForOpenOrReconnect(isOpenApp = false): OnyxData {
  const defaultData = {
    optimisticData: [],
    finallyData: [],
    // Possibly add data for sessions
    //   {
    //     onyxMethod: Onyx.METHOD.MERGE,
    //     key: ONYXKEYS.IS_LOADING_SESSION_DATA,
    //     value: true,
    //   },
    // ],
    // finallyData: [
    //   {
    //     onyxMethod: Onyx.METHOD.MERGE,
    //     key: ONYXKEYS.IS_LOADING_SESSION_DATA,
    //     value: false,
    //   },
    // ],
  };
  if (!isOpenApp) {
    return defaultData;
  }
  return {
    optimisticData: [
      ...defaultData.optimisticData,
      {
        onyxMethod: Onyx.METHOD.MERGE,
        key: ONYXKEYS.IS_LOADING_APP,
        value: true,
      },
    ],
    // `USER_DATA_HYDRATED` is set in `successData`, NOT `finallyData`, on
    // purpose: it is the readiness gates' positive proof that this session's
    // `app/open` actually delivered the user record. `finallyData` runs even
    // when the request is cancelled or fails fast (OpenApp is `canCancel`), so
    // pairing the flag with it would re-introduce the exact race we are closing
    // — the flag would flip true with no record in hand, and the onboarding
    // gate would then read a stale/persisted `USER_DATA_LIST` skeleton.
    successData: [
      {
        onyxMethod: Onyx.METHOD.MERGE,
        key: ONYXKEYS.USER_DATA_HYDRATED,
        value: true,
      },
    ],
    finallyData: [
      ...defaultData.finallyData,
      {
        onyxMethod: Onyx.METHOD.MERGE,
        key: ONYXKEYS.IS_LOADING_APP,
        value: false,
      },
    ],
  };
}

/**
 * Reset the per-session bootstrap signals on cold launch. Both keys are
 * persisted, so without this a value from a PREVIOUS session survives the
 * restart and the onboarding / terms readiness gates open before this launch's
 * `openApp` has even been dispatched — re-exposing the partial-hydration window
 * those gates exist to close:
 *   - `IS_LOADING_APP`: a stale `false` reads as "THIS session's bootstrap
 *     finished".
 *   - `USER_DATA_HYDRATED`: a stale `true` reads as "this session's app/open
 *     already delivered the user record", letting the gate act on a persisted
 *     `USER_DATA_LIST` skeleton (e.g. left behind when a force-quit skipped the
 *     sign-out `cleanupSession`).
 * Can't go in `initialKeyStates`: Onyx drops `null` defaults
 * (shouldRemoveNestedNulls), so the persisted value would survive hydration.
 * Called from `setup` before the React tree mounts.
 */
function resetBootstrapStateForColdLaunch(): void {
  Onyx.merge(ONYXKEYS.IS_LOADING_APP, null);
  Onyx.set(ONYXKEYS.USER_DATA_HYDRATED, false);
}

/**
 * Fetches data needed for app initialization
 */
function openApp() {
  // getPolicyParamsForOpenOrReconnect().then(
  // (policyParams: PolicyParamsForOpenOrReconnect) => {
  const params: OpenAppParams = {
    enablePriorityModeFilter: true,
    // ...policyParams,
  };
  API.write(
    WRITE_COMMANDS.OPEN_APP,
    params,
    getOnyxDataForOpenOrReconnect(true),
  );
  // },
  // );
}

/**
 * Fetches data when the app reconnects to the network
 * @param [updateIDFrom] the ID of the Onyx update that we want to start fetching from
 */
function reconnectApp(updateIDFrom: OnyxEntry<number> = 0) {
  console.debug(
    `[OnyxUpdates] App reconnecting with updateIDFrom: ${updateIDFrom}`,
  );
  // getPolicyParamsForOpenOrReconnect().then(policyParams => {
  //   const params: ReconnectAppParams = policyParams;
  const params: ReconnectAppParams = {};

  //   // When the app reconnects we do a fast "sync" of the LHN and only return chats that have new messages. We achieve this by sending the most recent reportActionID.
  //   // we have locally. And then only update the user about chats with messages that have occurred after that reportActionID.
  //   //
  //   // - Look through the local report actions and reports to find the most recently modified report action or report.
  //   // - We send this to the server so that it can compute which new chats the user needs to see and return only those as an optimization.
  //   Timing.start(CONST.TIMING.CALCULATE_MOST_RECENT_LAST_MODIFIED_ACTION);
  //   params.mostRecentReportActionLastModified =
  //     ReportActionsUtils.getMostRecentReportActionLastModified();
  //   Timing.end(
  //     CONST.TIMING.CALCULATE_MOST_RECENT_LAST_MODIFIED_ACTION,
  //     '',
  //     500,
  //   );

  // Include the update IDs when reconnecting so that the server can send incremental updates if they are available.
  // Otherwise, a full set of app data will be returned.
  if (updateIDFrom) {
    params.updateIDFrom = updateIDFrom;
  }

  API.write(
    WRITE_COMMANDS.RECONNECT_APP,
    params,
    getOnyxDataForOpenOrReconnect(),
    {
      checkAndFixConflictingRequest: persistedRequests =>
        resolveDuplicationConflictAction(
          persistedRequests,
          request => request.command === WRITE_COMMANDS.RECONNECT_APP,
        ),
    },
  );
  // });
}

/**
 * Fetches data when the app will call reconnectApp without params for the last time. This is a separate function
 * because it will follow patterns that are not recommended so we can be sure we're not putting the app in a unusable
 * state because of race conditions between reconnectApp and other pusher updates being applied at the same time.
 */
function finalReconnectAppAfterActivatingReliableUpdates(): Promise<void | OnyxTypes.Response> {
  console.debug(`[OnyxUpdates] Executing last reconnect app with promise`);

  // return getPolicyParamsForOpenOrReconnect().then(policyParams => {
  // const params: ReconnectAppParams = {...policyParams};
  const params: ReconnectAppParams = {};

  // When the app reconnects we do a fast "sync" of the LHN and only return chats that have new messages. We achieve this by sending the most recent reportActionID.
  // we have locally. And then only update the user about chats with messages that have occurred after that reportActionID.
  //
  // - Look through the local report actions and reports to find the most recently modified report action or report.
  // - We send this to the server so that it can compute which new chats the user needs to see and return only those as an optimization.
  // Timing.start(CONST.TIMING.CALCULATE_MOST_RECENT_LAST_MODIFIED_ACTION);
  // params.mostRecentReportActionLastModified =
  //   ReportActionsUtils.getMostRecentReportActionLastModified();
  // Timing.end(
  //   CONST.TIMING.CALCULATE_MOST_RECENT_LAST_MODIFIED_ACTION,
  //   '',
  //   500,
  // );

  // It is SUPER BAD FORM to return promises from action methods.
  // DO NOT FOLLOW THIS PATTERN!!!!!
  // It was absolutely necessary in order to not break the app while migrating to the new reliable updates pattern. This method will be removed
  // as soon as we have everyone migrated to the reliableUpdate beta.
  // eslint-disable-next-line rulesdir/no-api-side-effects-method
  return API.makeRequestWithSideEffects(
    SIDE_EFFECT_REQUEST_COMMANDS.RECONNECT_APP,
    params,
    getOnyxDataForOpenOrReconnect(),
  );
  // });
}

/**
 * Fetches data when the client has discovered it missed some Onyx updates from the server
 * @param [updateIDFrom] the ID of the Onyx update that we want to start fetching from
 * @param [updateIDTo] the ID of the Onyx update that we want to fetch up to
 */
function getMissingOnyxUpdates(
  updateIDFrom = 0,
  updateIDTo: number | string = 0,
): Promise<void | OnyxTypes.Response> {
  console.debug(
    `[OnyxUpdates] Fetching missing updates updateIDFrom: ${updateIDFrom} and updateIDTo: ${updateIDTo}`,
  );

  const parameters: GetMissingOnyxMessagesParams = {
    updateIDFrom,
    updateIDTo,
  };

  // It is SUPER BAD FORM to return promises from action methods.
  // DO NOT FOLLOW THIS PATTERN!!!!!
  // It was absolutely necessary in order to block OnyxUpdates while fetching the missing updates from the server or else the udpates aren't applied in the proper order.
  // eslint-disable-next-line rulesdir/no-api-side-effects-method
  return API.makeRequestWithSideEffects(
    SIDE_EFFECT_REQUEST_COMMANDS.GET_MISSING_ONYX_MESSAGES,
    parameters,
    getOnyxDataForOpenOrReconnect(),
  );
}

/**
 * This promise is used so that deeplink component know when a transition is end.
 * This is necessary because we want to begin deeplink redirection after the transition is end.
 */
let resolveSignOnTransitionToFinishPromise: () => void;
const signOnTransitionToFinishPromise = new Promise<void>(resolve => {
  resolveSignOnTransitionToFinishPromise = resolve;
});

function waitForSignOnTransitionToFinish(): Promise<void> {
  return signOnTransitionToFinishPromise;
}

function endSignOnTransition() {
  return resolveSignOnTransitionToFinishPromise();
}

/**
 * This action runs when the Navigator is ready and the current route changes
 *
 * currentPath should be the path as reported by the NavigationContainer
 *
 * The transition link contains an exitTo param that contains the route to
 * navigate to after the user is signed in. A user can transition from OldDot
 * with a different account than the one they are currently signed in with, so
 * we only navigate if they are not signing in as a new user. Once they are
 * signed in as that new user, this action will run again and the navigation
 * will occur.

 * When the exitTo route is 'workspace/new', we create a new
 * workspace and navigate to it
 *
 * We subscribe to the session using withOnyx in the AuthScreens and
 * pass it in as a parameter. withOnyx guarantees that the value has been read
 * from Onyx because it will not render the AuthScreens until that point.
 */
function setUpPoliciesAndNavigate(user: User | null) {
  const currentUrl = getCurrentUrl();
  if (!user || !currentUrl?.includes('exitTo')) {
    return;
  }

  const url = new URL(currentUrl);
  const exitTo = url.searchParams.get('exitTo') as Route | null;

  // A transition link carries the account it was minted for alongside the exitTo route. If that
  // account is not the one we are signed in as, the route belongs to the other user, so we skip it
  // here: this action runs again once that user is signed in. A link with no email param is for
  // whoever is signed in, so it goes through.
  const transitionEmail = url.searchParams.get('email');
  const isLoggingInAsNewUser =
    !!transitionEmail &&
    transitionEmail.toLowerCase() !== (user.email ?? '').toLowerCase();

  // Approved Accountants and Guides can enter a flow where they make a workspace for other users,
  // and those are passed as a search parameter when using transition links
  // const policyOwnerEmail = url.searchParams.get('ownerEmail') ?? '';
  // const makeMeAdmin = !!url.searchParams.get('makeMeAdmin');
  // const policyName = url.searchParams.get('policyName') ?? '';

  // Sign out the current user if we're transitioning with a different user
  // const isTransitioning = Str.startsWith(
  //   url.pathname,
  //   Str.normalizeUrl(ROUTES.TRANSITION_BETWEEN_APPS),
  // );

  // const shouldCreateFreePolicy =
  // !isLoggingInAsNewUser && isTransitioning && exitTo === ROUTES.WORKSPACE_NEW;
  // if (shouldCreateFreePolicy) {
  //   createWorkspaceWithPolicyDraftAndNavigateToIt(
  //     policyOwnerEmail,
  //     policyName,
  //     true,
  //     makeMeAdmin,
  //   );
  //   return;
  // }
  if (!isLoggingInAsNewUser && exitTo) {
    Navigation.waitForProtectedRoutes()
      .then(() => {
        // We must call goBack() to remove the /transition route from history
        Navigation.goBack();
        Navigation.navigate(exitTo);
      })
      .then(endSignOnTransition);
  }
}

function redirectThirdPartyDesktopSignIn() {
  const currentUrl = getCurrentUrl();
  if (!currentUrl) {
    return;
  }
  const url = new URL(currentUrl);

  if (
    url.pathname === `/${ROUTES.GOOGLE_SIGN_IN}` ||
    url.pathname === `/${ROUTES.APPLE_SIGN_IN}`
  ) {
    Navigation.isNavigationReady().then(() => {
      Navigation.goBack();
      Navigation.navigate(ROUTES.DESKTOP_SIGN_IN_REDIRECT);
    });
  }
}

// function openProfile(personalDetails: OnyxTypes.UserData) {
//   const oldTimezoneData = personalDetails.timezone ?? {};
//   let newTimezoneData = oldTimezoneData;

//   if (oldTimezoneData?.automatic ?? true) {
//     newTimezoneData = {
//       automatic: true,
//       selected: Intl.DateTimeFormat().resolvedOptions()
//         .timeZone as SelectedTimezone,
//     };
//   }

//   newTimezoneData = DateUtils.formatToSupportedTimezone(newTimezoneData);

//   const parameters: OpenProfileParams = {
//     timezone: JSON.stringify(newTimezoneData),
//   };

//   // We expect currentUserID to be a number because it doesn't make sense to open profile if currentUserID is not set
//   if (typeof currentUserID === 'number') {
//     API.write(WRITE_COMMANDS.OPEN_PROFILE, parameters, {
//       optimisticData: [
//         {
//           onyxMethod: Onyx.METHOD.MERGE,
//           key: ONYXKEYS.USER_DATA_LIST,
//           value: {
//             [currentUserID]: {
//               timezone: newTimezoneData,
//             },
//           },
//         },
//       ],
//       failureData: [
//         {
//           onyxMethod: Onyx.METHOD.MERGE,
//           key: ONYXKEYS.USER_DATA_LIST,
//           value: {
//             [currentUserID]: {
//               timezone: oldTimezoneData,
//             },
//           },
//         },
//       ],
//     });
//   }
// }

// /**
//  * @param shouldAuthenticateWithCurrentAccount Optional, indicates whether default authentication method (shortLivedAuthToken) should be used
//  */
// function beginDeepLinkRedirect(shouldAuthenticateWithCurrentAccount = true) {
//   // There's no support for anonymous users on desktop
//   if (Session.isAnonymousUser()) {
//     return;
//   }

//   // If the route that is being handled is a magic link, email and shortLivedAuthToken should not be attached to the url
//   // to prevent signing into the wrong account
//   if (!currentUserID || !shouldAuthenticateWithCurrentAccount) {
//     Browser.openRouteInDesktopApp();
//     return;
//   }

//   const parameters: OpenOldDotLinkParams = {shouldRetry: false};

//   // eslint-disable-next-line rulesdir/no-api-side-effects-method
//   API.makeRequestWithSideEffects(
//     SIDE_EFFECT_REQUEST_COMMANDS.OPEN_OLD_DOT_LINK,
//     parameters,
//     {},
//   ).then(response => {
//     if (!response) {
//       Log.alert(
//         'Trying to redirect via deep link, but the response is empty. User likely not authenticated.',
//         {response, shouldAuthenticateWithCurrentAccount, currentUserID},
//         true,
//       );
//       return;
//     }

//     Browser.openRouteInDesktopApp(
//       response.shortLivedAuthToken,
//       currentUserEmail,
//     );
//   });
// }

// /**
//  * @param shouldAuthenticateWithCurrentAccount Optional, indicates whether default authentication method (shortLivedAuthToken) should be used
//  */
// function beginDeepLinkRedirectAfterTransition(
//   shouldAuthenticateWithCurrentAccount = true,
// ) {
//   waitForSignOnTransitionToFinish().then(() =>
//     beginDeepLinkRedirect(shouldAuthenticateWithCurrentAccount),
//   );
// }

function updateLastVisitedPath(path: string) {
  Onyx.merge(ONYXKEYS.LAST_VISITED_PATH, path);
}

function updateLastRoute(screen: string) {
  Onyx.set(ONYXKEYS.LAST_ROUTE, screen);
}

async function setLoadingText(
  text: OnyxMergeInput<'appLoadingText'>,
): Promise<void> {
  await Onyx.merge(ONYXKEYS.APP_LOADING_TEXT, text);
}

/** Record the day the viewer is currently looking at in `userID`'s enlarged
 *  calendar / day-overview scroll. Stored per viewed user so backing out to that
 *  user's profile (or home, for self) lands on the same date, while a different
 *  user's calendar is never repointed (Rule 2). Merge (not set) so other users'
 *  entries are preserved. */
function setLastViewedCalendarDate(userID: UserID, date: DateString) {
  Onyx.merge(ONYXKEYS.NVP_LAST_VIEWED_CALENDAR_DATE, {[userID]: date});
}

/** Clear `userID`'s consumed last-viewed date so subsequent focuses don't
 *  re-apply it (e.g. after manual month navigation on that user's calendar). The
 *  nested null deletes only that user's entry, leaving every other user's
 *  untouched. The cold-launch reset of the WHOLE map lives in
 *  `Calendar.resetCalendarStateForColdLaunch`. */
function clearLastViewedCalendarDate(userID: UserID) {
  Onyx.merge(ONYXKEYS.NVP_LAST_VIEWED_CALENDAR_DATE, {[userID]: null});
}

export {
  setLoadingText,
  setLastViewedCalendarDate,
  clearLastViewedCalendarDate,
  setLocale,
  setLocaleAndNavigate,
  setSidebarLoaded,
  setUpPoliciesAndNavigate,
  // openProfile,
  redirectThirdPartyDesktopSignIn,
  isReadyToOpenApp,
  openApp,
  resetBootstrapStateForColdLaunch,
  reconnectApp,
  confirmReadyToOpenApp,
  // beginDeepLinkRedirect,
  // beginDeepLinkRedirectAfterTransition,
  getMissingOnyxUpdates,
  finalReconnectAppAfterActivatingReliableUpdates,
  updateLastVisitedPath,
  updateLastRoute,
  waitForSignOnTransitionToFinish,
  KEYS_TO_PRESERVE,
};
